/**
 * Project Members API service for managing project team membership.
 * @module lib/api/project-members.api
 */

import { PaginatedResponse } from '@scrambl/shared'

import { apiClient } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface ProjectMember {
  id: string
  projectId: string
  userId?: string | null
  memberId?: string | null
  role: string
  status: string
  settings: Record<string, unknown>
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    displayName: string | null
    firstName: string | null
    lastName: string | null
    email: string
    image?: string
  }
}

export interface CreateProjectMemberRequest {
  userId?: string
  memberId?: string
  roleKey?: string
  role?: string
  status?: string
  settings?: Record<string, unknown>
}

export interface ProjectMemberListParams {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

class ProjectMemberApiService {
  /**
   * Returns the base URL for project member endpoints
   */
  private getBaseUrl(projectId: string): string {
    return `/projects/${projectId}/members`
  }

  /**
   * Gets all members of a project with pagination
   * @param projectId - The project ID
   * @param params - Pagination and sorting options
   * @returns Paginated list of project members
   */
  getProjectMembers(
    projectId: string,
    params: ProjectMemberListParams = {},
  ): Promise<PaginatedResponse<ProjectMember>> {
    const queryString = buildQueryString(params)
    return apiClient<PaginatedResponse<ProjectMember>>(
      `${this.getBaseUrl(projectId)}${queryString}`,
    )
  }

  /**
   * Adds a new member to a project
   * @param projectId - The project ID
   * @param data - Member creation data
   * @returns The created project member
   */
  addMember(
    projectId: string,
    data: CreateProjectMemberRequest,
  ): Promise<ProjectMember> {
    return apiClient<ProjectMember>(this.getBaseUrl(projectId), {
      method: 'POST',
      body: data,
    })
  }

  /**
   * Updates an existing project member
   * @param projectId - The project ID
   * @param memberId - The member ID to update
   * @param data - Member update data
   * @returns The updated project member
   */
  updateMember(
    projectId: string,
    memberId: string,
    data: Partial<CreateProjectMemberRequest>,
  ): Promise<ProjectMember> {
    return apiClient<ProjectMember>(
      `${this.getBaseUrl(projectId)}/${memberId}`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  }

  /**
   * Removes a member from a project
   * @param projectId - The project ID
   * @param memberId - The member ID to remove
   */
  removeMember(projectId: string, memberId: string): Promise<void> {
    return apiClient<void>(`${this.getBaseUrl(projectId)}/${memberId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Gets a specific member of a project
   * @param projectId - The project ID
   * @param memberId - The member ID
   * @returns The project member details
   */
  getMember(projectId: string, memberId: string): Promise<ProjectMember> {
    return apiClient<ProjectMember>(`${this.getBaseUrl(projectId)}/${memberId}`)
  }
}

export const projectMemberApi = createSingletonApi(ProjectMemberApiService)
